import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentStatusBanner = () => {
  const [state, setState] = useState({ visible: false, kind: "pending", title: "", message: "" });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    const canceled = url.searchParams.get("canceled");

    if (canceled) {
      setState({
        visible: true,
        kind: "error",
        title: "Checkout canceled",
        message: "No worries — the pixies are still here when you're ready.",
      });
      url.searchParams.delete("canceled");
      window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : ""));
      return;
    }

    if (!sessionId) return;
    setState({
      visible: true,
      kind: "pending",
      title: "Checking your order…",
      message: "Hold a leaf, the pixies are confirming it.",
    });

    const poll = async () => {
      attemptsRef.current += 1;
      if (attemptsRef.current > 8) {
        setState({
          visible: true,
          kind: "error",
          title: "Still checking…",
          message: "Check your email for confirmation, or refresh the page.",
        });
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const ps = res.data?.payment_status;
        if (ps === "paid") {
          setState({
            visible: true,
            kind: "success",
            title: "Magic complete!",
            message: "Your book is on its way. Welcome to Miara Mountain. ✿",
          });
          // clear url
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams.toString()}` : ""));
          return;
        }
        if (res.data?.status === "expired") {
          setState({
            visible: true,
            kind: "error",
            title: "Session expired",
            message: "Please try the checkout again.",
          });
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch (e) {
        console.error(e);
        timerRef.current = setTimeout(poll, 2500);
      }
    };

    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const close = () => setState((s) => ({ ...s, visible: false }));

  const Icon =
    state.kind === "success" ? CheckCircle2 : state.kind === "error" ? XCircle : Loader2;

  return (
    <AnimatePresence>
      {state.visible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          data-testid="payment-banner"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[70] w-[calc(100vw-2rem)] max-w-md glass-card px-5 py-4 flex items-start gap-3"
        >
          <Icon
            size={22}
            className={`mt-0.5 ${
              state.kind === "success"
                ? "text-emerald-300"
                : state.kind === "error"
                ? "text-rose-300"
                : "text-amber-300 animate-spin"
            }`}
          />
          <div className="flex-1">
            <div className="font-heading text-white text-lg leading-tight">{state.title}</div>
            <div className="text-sm text-emerald-50/75 mt-1">{state.message}</div>
          </div>
          <button onClick={close} className="text-emerald-50/60 hover:text-amber-300" aria-label="Close">
            <X size={16} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentStatusBanner;
